import type { TierSelection } from '@/lib/mapping';

export type State = {
  address?: Address;
  autocompletions: {
    zipCodes: Array<{ name: string; zipCode: string; district?: string }>;
    cities: Array<{ name: string; zipCode: string; district?: string }>;
    streets: Array<{ name: string; streetNumber: string }>;
  };
  alertMessage: string;
  alertTitle: string;
  challengeRequired: Record<string, unknown>;
  bi: Bi;
  bundle: Bundle | null;
  currentPage: string;
  dialog: Dialog;
  disableOnSubmit: boolean;
  eligibleForTrial?: boolean;
  entryPage: string;
  hideAlert: boolean;
  isFreePurchase: boolean;
  isLoggedIn: boolean;
  order?: {
    orderId?: string;
  };
  payment: Payment;
  pageTheme: string;
  pricingText: string;
  printAccountNumber?: string;
  productClass: string;
  restoredFromStorage: boolean;
  showProductOnRight: boolean;
  showVoucherInput: boolean;
  supporter: Supporter;
  upgrade: Upgrade;
  user: User;
  voucher?: Voucher;
};

type Address = {
  delivery: {
    city: string;
    country?: string;
    door?: string;
    firstName?: string;
    floor?: string;
    houseNumber?: string;
    lastName?: string;
    number?: string;
    stairs?: string;
    stairway?: string;
    street: string;
    streetID: string;
    title?: string;
    zip: string;
  };
  deliveryInformationReference?: string;
};

type Dialog = {
  actions?: Array<{
    className?: string;
    label?: string;
    onClick: () => void;
  }>;
  body: string;
  isHidden: boolean;
  title: string;
  type?: 'confirm' | 'alert';
};

type User = {
  acceptMassmail: boolean;
  email?: string;
  ip: string;
  tokenID: string;
  password?: string;
  phone: {
    number?: string;
    countryCode?: string;
  };
};

type Bundle = {
  bundleReference: string;
  cartReference: string;
  containsPhysical: boolean;
  contractAllowedPaymentMethods: string[];
  contractCustomParameters: ContractCustomParameter[];
  contractDescription: string;
  contractInfo: ContractInfo;
  contractReference: string;
  contractSubscriptionGroup: string;
  contractTrialInfo: ContractTrialInfo;
  description: string;
  lineItems: LineItem[];
  name: string;
  serviceImage: string;
  sumGrossAmount: number;
  sumTrialGrossAmount: number;
};

type ContractTrialInfo = {
  discountPercentage: number;
  nonPaymentTrial: boolean;
  trialChargedAsSinglePeriod: boolean;
  trialEnabled: boolean;
  trialPeriod: number;
  trialUnit: string;
};

type ContractInfo = {
  autoRenewDefault: string;
  contractPeriod: number;
  contractUnit: string;
};

type ContractCustomParameter = {
  parameterName: string;
  parameterType: string;
  parameterValue: string;
};

type LineItem = {
  description: string;
  grossAmount: number;
  lineItemReference: string;
  product: Product;
};

type Product = {
  description: string;
  name: string;
  productReference: string;
  imageLocation?: string;
};

type Payment = {
  accountReference: string;
  paymentMethod: string;
  reusePaymentMethod: boolean;
  sessionToken: string;
};

type Supporter = {
  minimumAmount: number;
  selection: Selection;
  tiers: Tiers;
};

type Tiers = {
  high: string;
  low: string;
  mid: string;
};

type Selection = {
  period: TierSelection;
  tier: string;
};

type Bi = {
  userToken: boolean;
};

type Upgrade = {
  message: string;
  activeCountryCode?: string;
  activePaymentAccount?: string;
  activePaymentMethod?: string;
  isMovableSubscription?: boolean;
  movableSubscription: boolean;
  moveFromBundleRef?: string;
  moveFromSubscriptionRef?: string;
  requirePrintAccountNumber?: boolean;
};

type Voucher = {
  isValid: boolean;
  purchaseInfo: {
    purchasePrice: number;
    discountPrice: number;
    renewalPrice: number;
  };
  offerInfo: {
    name: string;
    usageType: number;
    lowStart: {
      percentage: number;
      numberOfPeriods: number;
      lockInPeriods: number;
      closeSubOnExpiry: boolean;
      serviceReferences: Array<string>;
      noPaymentDetailsRequired: boolean;
      paymentDetailsRequired: boolean;
    };
  };
  code: string;
};

type KeyValuePair = {
  key: string;
  value: unknown;
};

type ActionMap<M extends { [index: string]: unknown }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

export enum ActionTypes {
  addressChange = 'ADDRESS_CHANGE',
  addressVerification = 'ADDRESS_VERIFICATION',
  bundleData = 'BUNDLE_DATA',
  challengeRequired = 'CHALLENGE_REQUIRED',
  credentialsChange = 'CREDENTIALS_CHANGE',
  disableForm = 'DISABLE_FORM',
  enableForm = 'ENABLE_FORM',
  hideDialog = 'HIDE_DIALOG',
  hideError = 'HIDE_ERROR',
  initSupporter = 'INIT_SUPPORTER',
  isLoggedIn = 'IS_LOGGED_IN',
  printAccountNumberChange = 'PRINTACCOUNTNUMBER_CHANGE',
  purchaseCompleted = 'PURCHASE_COMPLETED',
  registerLegal = 'REGISTER_LEGAL',
  removeVoucher = 'REMOVE_VOUCHER',
  resetCompletions = 'RESET_COMPLETIONS',
  restoreFromStorage = 'RESTORE_FROM_STORAGE',
  setChallengeV2Data = 'SET_CHALLENGE_V2_DATA',
  setCurrentPage = 'SET_CURRENT_PAGE',
  setEligibleForTrial = 'SET_ELIGIBLE_FOR_TRIAL',
  setEntryPage = 'SET_ENTRY_PAGE',
  setEmail = 'SET_EMAIL',
  setFreePurchase = 'SET_FREEPURCHASE',
  setMovableSubscription = 'SET_MOVABLE_SUBSCRIPTION',
  setMppCredentials = 'SET_MPP_CREDENTIALS',
  setPaymentMethod = 'SET_PAYMENT_METHOD',
  setPhoneNumber = 'SET_PHONE_NUMBER',
  setPricingText = 'SET_PRICINGTEXT',
  setProductClass = 'SET_PRODUCT_CLASS',
  setReusePaymentMethod = 'SET_REUSE_PAYMENT_METHOD',
  setSectionClass = 'SET_SECTION_CLASS',
  setStreetId = 'SET_STREET_ID',
  setTokenId = 'SET_TOKEN_ID',
  setVoucher = 'SET_VOUCHER',
  showDialog = 'SHOW_DIALOG',
  showError = 'SHOW_ERROR',
  showProductOnRight = 'SHOW_PRODUCT_ON_RIGHT',
  supporterChangePeriod = 'SUPPORTER_CHANGE_PERIOD',
  supporterChangeTier = 'SUPPORTER_CHANGE_TIER',
  supporterCustomMinimumAmount = 'SUPPORTER_CUSTOM_MINIMUM_AMOUNT',
  toggleVoucherInput = 'TOGGLE_VOUCHER_INPUT',
  zipCity = 'ZIP_CITY'
}

type ActionPayload = {
  [ActionTypes.isLoggedIn]: boolean;

  [ActionTypes.toggleVoucherInput]: boolean;

  [ActionTypes.showError]:
    | string
    | {
        title: string;
        message: string;
      };

  [ActionTypes.hideError];

  [ActionTypes.setEmail]: string;

  [ActionTypes.showProductOnRight]: boolean;

  [ActionTypes.setProductClass]: string;

  [ActionTypes.setPricingText]: string;

  [ActionTypes.setVoucher]: {
    code: string;
  };

  [ActionTypes.setFreePurchase]: boolean;
  [ActionTypes.setEligibleForTrial]: boolean;

  [ActionTypes.removeVoucher];

  [ActionTypes.setMovableSubscription]: Upgrade;

  [ActionTypes.setPaymentMethod]: string;
  [ActionTypes.setReusePaymentMethod]: boolean;

  [ActionTypes.addressChange]: { key: string; value: unknown };
  [ActionTypes.addressVerification]: { key: string; value: unknown };
  [ActionTypes.bundleData]: { key: string; value: unknown };
  [ActionTypes.challengeRequired]: { key: string; value: unknown };
  [ActionTypes.credentialsChange]: { key: string; value: unknown };
  [ActionTypes.disableForm]: { key: string; value: unknown };
  [ActionTypes.enableForm]: { key: string; value: unknown };
  [ActionTypes.printAccountNumberChange]: { key: string; value: unknown };
  [ActionTypes.registerLegal]: { key: string; value: unknown };
  [ActionTypes.resetCompletions]: { key: string; value: unknown };
  [ActionTypes.setCurrentPage]: { key: string; value: unknown };
  [ActionTypes.setChallengeV2Data]: { key: string; value: unknown };
  [ActionTypes.setEntryPage]: { key: string; value: unknown };
  [ActionTypes.setStreetId]: { key: string; value: unknown };
  [ActionTypes.setTokenId]: { key: string; value: unknown };
  [ActionTypes.supporterChangeTier]: { key: string; value: unknown };
  [ActionTypes.supporterCustomMinimumAmount]: { key: string; value: unknown };

  [ActionTypes.initSupporter]: {
    minimumAmount: string;
    period: TierSelection;
    tier: string;
    tiers: Tiers;
  };

  [ActionTypes.setPhoneNumber]: {
    number: string;
    countryCode: string;
  };

  [ActionTypes.zipCity]: {
    city?: string;
    zip?: string;
  };

  [ActionTypes.purchaseCompleted]: {
    order: Record<string, unknown>;
  };

  [ActionTypes.setMppCredentials]: {
    sessionToken: string;
    accountReference: string;
    clientIp: string;
  };

  [ActionTypes.supporterChangePeriod]: {
    selection: TierSelection;
    tiers: Tiers;
  };

  [ActionTypes.restoreFromStorage]: State;

  [ActionTypes.hideDialog];

  [ActionTypes.showDialog]: Omit<Dialog, 'isHidden'> & {
    title?: string;
    body?: string;
  };

  [ActionTypes.setSectionClass]: 'pur' | 'supporter';
};

type Action = ActionMap<ActionPayload>[keyof ActionMap<ActionPayload>];
